import { IconBatteryAutomotive, IconBrandAsana, IconBrandSuperhuman, IconCategory, IconChevronsLeft, IconChevronsRight, IconDashboard, IconGitFork, IconHome, IconSettings, IconSquare, IconSquaresFilled, IconUser, IconUsers } from '@tabler/icons-react'
import React from 'react'
import { NavLink } from 'react-router-dom'
import { useSideNavAPI } from '../API/sideNavAPI'
import { ScrollArea } from '../shadcn/ui/scroll-area'
import { ButtonIconSquare } from './buttons'
import { useGetAdminByToken } from 'src/API/rest.authentication'
import { useCentralAPI } from 'src/API/centralAPI'

export interface NavbarButtonProps {
    icon: React.ReactNode,
    text: string,
    isSelected: boolean,
    href: string,
    isCollapsed?: boolean
}

export function NavbarButton({
    icon,
    text,
    isSelected,
    href,
    isCollapsed
}: NavbarButtonProps) {
    const [isActive, setActive] = React.useState(false)

    return (
        <NavLink 
            to={href} 
            className={({ isActive: state }) => {
                setActive(state)
                return `
                    flex items-center gap-3  py-2.5 rounded-lg transition-all duration-200
                    ${isCollapsed ? 'justify-center' : 'justify-start px-3'}
                    ${state ? 'bg-primary/10 text-primary' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}
                `
            }}
        >
            <div className={`
                flex items-center justify-center w-8 h-8 rounded-lg
                ${isActive ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-800'}
            `}>
                {icon}
            </div>
            {!isCollapsed && (
                <span className="text-sm font-medium truncate">
                    {text}
                </span>
            )}
        </NavLink>
    )
}

export default function SideNavbar() {
    const {
        hideSideNav,
        collapseSideNav,
        showSideNav,
        unCollapseSideNav,
        sideNavState,
        sideNavCollapseState
    } = useSideNavAPI()

    // const configuration = useConfigurationRQ()
      const admin = useGetAdminByToken({trigger:false})
      const {
        centralData
      }= useCentralAPI({trigger:false})
    
    //   const adminData= centralData?.Users.find((user) => user.id.includes(admin.adminData?.id || ''))
    //   const originId = adminData?.user.originId

    const commonNavBar: NavbarButtonProps[] = [
        {
            icon: <IconHome size={20} />,
            text: 'Dashboard',
            isSelected: true,
            href: '/home/dashboard'
        },
        {
            icon: <IconDashboard size={20} />,
            text: 'User Dashboard',
            isSelected: true,
            href: `master/employee/${admin.adminData?.id}`
        },
    ]

    const MasterSideNavBars: NavbarButtonProps[] = [
        {
            icon: <IconUsers size={20} />,
            text: 'Contributors',
            isSelected: false,
            href: 'master/contributors'
        },
        {
            icon: <IconUsers size={20} />,
            text: 'Tasks',
            isSelected: false,
            href: 'master/tasks'
        },
        {
            icon: <IconBrandAsana size={20} />,
            text: 'Projects',
            isSelected: false,
            href: 'master/Projects'
        },
        {
            icon: <IconBatteryAutomotive size={20} />,
            text: 'Capacity',
            isSelected: false,
            href: 'master/capacity'
        },
        {
            icon: <IconGitFork size={20} />,
            text: 'My works',
            isSelected: false,
            href: 'master/my_works'
        },
    ]

    return (
        <>
            <nav 
                style={{
                    left: sideNavState === 'open' ? '0' : '-280px',
                }} 
                className={`
                    fixed sm:sticky top-0 bottom-0 z-50
                    transition-all h-full duration-300 ease-in-out
                    ${sideNavCollapseState === 'open' ? 'w-[280px]' : 'w-[80px]'}
                    bg-white dark:bg-gray-900
                    border-r border-gray-200 dark:border-gray-800
                    shadow-lg sm:shadow-none
                `}
            >
                <ScrollArea className="h-full">
                    <div className="flex flex-col h-full p-4">
                        {/* Logo */}
                        <div className="flex justify-center py-6">
                            {sideNavCollapseState === 'open' ? (
                                <img className="h-8" src="/assets/logo/logo_big.webp" alt="Logo" />
                            ) : (
                                <img className="h-8" src="/assets/logo/logo_small.webp" alt="Logo" />
                            )}
                        </div>

                        {/* Navigation */}
                        <div className="flex-1 space-y-6">
                            {/* Common Navigation */}
                            <div className="space-y-1">
                                {commonNavBar.map((item, index) => (
                                    <NavbarButton 
                                        key={index}
                                        {...item}
                                        isCollapsed={sideNavCollapseState === 'close'}
                                    />
                                ))}
                            </div>

                            {/* Master Navigation */}
                            <div className="space-y-1">
                                <div className="px-3 mb-2">
                                    {sideNavCollapseState === 'open' ? (
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Master
                                        </span>
                                    ) : (
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider text-center block">
                                            M
                                        </span>
                                    )}
                                </div>
                                {MasterSideNavBars.map((item, index) => (
                                    <NavbarButton 
                                        key={index}
                                        {...item}
                                        isCollapsed={sideNavCollapseState === 'close'}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Bottom Section */}
                        <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                            <div className={`
                                flex items-center  py-2
                                ${sideNavCollapseState === 'open' ? 'justify-between px-3 gap-3 ' : 'justify-center'}
                            `}>
                                <div className="flex items-center gap-3">
                                    <IconSettings size={20} className="text-gray-500" />
                                    {sideNavCollapseState === 'open' && (
                                        <span className="text-sm text-gray-500">Settings</span>
                                    )}
                                </div>
                                <button
                                    onClick={sideNavCollapseState === 'open' ? collapseSideNav : unCollapseSideNav}
                                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                                >
                                    {sideNavCollapseState === 'open' ? (
                                        <IconChevronsLeft size={20} className="text-gray-500" />
                                    ) : (
                                        <IconChevronsRight size={20} className="text-gray-500" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </ScrollArea>
            </nav>

            {/* Overlay */}
            {sideNavState === 'open' && (
                <div 
                    className="fixed inset-0 z-40 bg-black/50 sm:hidden"
                    onClick={hideSideNav}
                />
            )}
        </>
    )
}
